import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { BatchSlaRow } from "../components/BatchSlaTable";

// Raw backend row has `completedAt: Date` which serializes to ISO string.
// We convert to `string` for the view model.
interface RawBatchSlaRow {
  batchId: string;
  jobTypeId: string;
  completedAt: string;
  totalDurationMs: number;
  docCount: number;
  slaMet: boolean | null;
  slaTargetMs: number | null;
}

export type BatchSlaTier = "hot" | "cold";
export type BatchSlaBucket = "day" | "week" | "month";

export interface BatchSlaResponse {
  tier: BatchSlaTier;
  bucket: BatchSlaBucket;
  rows: RawBatchSlaRow[];
}

export interface UseBatchSlaData {
  tier: BatchSlaTier;
  bucket: BatchSlaBucket;
  rows: BatchSlaRow[];
}

export function useBatchSla(
  jobTypeId?: string,
  from?: string,
  to?: string,
  bucket?: BatchSlaBucket,
) {
  const [data, setData] = useState<UseBatchSlaData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (jobTypeId) params.set("jobTypeId", jobTypeId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<BatchSlaResponse>(`/analytics/batch-sla${query}`);
      setData({
        tier: result.tier,
        bucket: result.bucket,
        rows: result.rows.map((r) => ({ ...r })),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load batch SLA rows");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
